import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Seo from './Seo';
import '../Offers.css';

/* Répartition illustrative d'une facture cloud.
   w = part du poste dans la facture, s = part réductible (fin de barre). */
const BILL = [
  { label: 'Compute', w: 88, s: 26 },
  { label: 'Database', w: 62, s: 14 },
  { label: 'Storage', w: 44, s: 12 },
  { label: 'Network', w: 31, s: 9 },
  { label: 'Other', w: 22, s: 5 },
];

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

export default function AuditCloud() {
  const { t } = useTranslation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const trust = t('ac_trust', { returnObjects: true });
  const pains = t('ac_pains', { returnObjects: true });
  const steps = t('ac_steps', { returnObjects: true });
  const deliver = t('ac_deliver', { returnObjects: true });
  const scope = t('ac_scope', { returnObjects: true });
  const faq = t('ac_faq', { returnObjects: true });

  return (
    <div className="of_page">
      <Seo title={t('ac_seo_title')} description={t('ac_seo_desc')} path="/audit-cloud" />

      {/* ---------------- HERO ---------------- */}
      <section className="of_hero">
        <div className="of_wrap of_hero_grid">
          <div>
            <span className="of_eyebrow">{t('ac_eyebrow')}</span>
            <h1>
              {t('ac_title_1')}
              <span className="am">{t('ac_title_2')}</span>
            </h1>
            <p className="of_hero_sub">{t('ac_sub')}</p>

            <div className="of_cta_row">
              <Link className="of_btn of_btn_primary" to="/contactUs?subject=audit">
                {t('ac_cta1')} <span aria-hidden="true">→</span>
              </Link>
              <a className="of_btn of_btn_ghost" href="#process">{t('ac_cta2')}</a>
            </div>

            <div className="of_badge" style={{ marginBottom: 22 }}>
              <ShieldIcon /> {t('ac_badge')}
            </div>

            <div className="of_trust">
              {trust.map((item) => (
                <span key={item}><span className="tick">✓</span>{item}</span>
              ))}
            </div>
          </div>

          {/* panneau illustratif */}
          <div className="of_panel" aria-hidden="true">
            <div className="of_panel_bar">
              <span className="of_dot r" /><span className="of_dot y" /><span className="of_dot g" />
              <span className="of_panel_title">{t('ac_panel_title')}</span>
            </div>
            <div className="of_panel_body">
              <div className="of_bill">
                {BILL.map((row) => (
                  <div className="of_bill_row" key={row.label}>
                    <span className="lbl">{row.label}</span>
                    <span className="of_bar">
                      <i style={{ width: `${row.w}%` }} />
                      <b style={{ left: `${row.w - row.s}%`, width: `${row.s}%` }} />
                    </span>
                  </div>
                ))}
              </div>

              <div className="of_bill_legend">
                <span><i style={{ background: '#2f3d57' }} />{t('ac_legend_current')}</span>
                <span><i style={{ background: 'rgba(255,193,7,.75)' }} />{t('ac_legend_saving')}</span>
              </div>

              <div className="of_saving">
                <span className="big">{t('ac_saving_big')}</span>
                <span className="cap">{t('ac_saving_cap')}</span>
              </div>
            </div>
            <p className="of_panel_note">{t('ac_panel_note')}</p>
          </div>
        </div>
      </section>

      {/* ---------------- LE PROBLÈME ---------------- */}
      <section className="of_section alt">
        <div className="of_wrap">
          <div className="of_head">
            <span className="of_eyebrow">{t('ac_pains_eyebrow')}</span>
            <h2 className="of_h2">{t('ac_pains_h2')}</h2>
          </div>
          <div className="of_pains">
            {pains.map((p) => (
              <div className="of_pain" key={p}>
                <span className="of_pain_x">✕</span>
                <p>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- LA PROMESSE ---------------- */}
      <section className="of_section">
        <div className="of_wrap">
          <div className="of_head"><span className="of_eyebrow">{t('ac_promise_eyebrow')}</span></div>
          <div className="of_promise">
            <p className="q">{t('ac_promise')}</p>
            <p className="g">{t('ac_guarantee')}</p>
          </div>
        </div>
      </section>

      {/* ---------------- LE DÉROULÉ ---------------- */}
      <section className="of_section alt" id="process">
        <div className="of_wrap">
          <div className="of_head center">
            <span className="of_eyebrow">{t('ac_process_eyebrow')}</span>
            <h2 className="of_h2">{t('ac_process_h2')}</h2>
          </div>
          <div className="of_steps">
            {steps.map((s, i) => (
              <div className="of_step" key={s.title}>
                <div className="of_step_n">{i + 1}</div>
                <span className="when">{s.when}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- LIVRABLES + PÉRIMÈTRE ---------------- */}
      <section className="of_section">
        <div className="of_wrap of_two">
          <div>
            <span className="of_eyebrow">{t('ac_deliver_eyebrow')}</span>
            <h2 className="of_h2">{t('ac_deliver_h2')}</h2>
            <ul className="of_checks" style={{ marginTop: 22 }}>
              {deliver.map((d) => <li key={d}>{d}</li>)}
            </ul>
          </div>
          <div className="of_note">
            <h3>{t('ac_scope_h3')}</h3>
            <ul className="of_checks" style={{ marginTop: 18 }}>
              {scope.map((s) => <li key={s}>{s}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------------- TARIF ---------------- */}
      <section className="of_section alt">
        <div className="of_wrap">
          <div className="of_head center">
            <span className="of_eyebrow">{t('ac_price_eyebrow')}</span>
            <h2 className="of_h2">{t('ac_price_h2')}</h2>
          </div>

          <div className="of_prices">
            <div className="of_price feat">
              <span className="of_price_tag">{t('ac_price_ma_tag')}</span>
              <div className="amt">{t('ac_price_ma_amt')}</div>
              <p>{t('ac_price_desc')}</p>
            </div>
            <div className="of_price">
              <span className="of_price_tag">{t('ac_price_eu_tag')}</span>
              <div className="amt">{t('ac_price_eu_amt')}</div>
              <p>{t('ac_price_desc')}</p>
            </div>
          </div>

          <div className="of_note" style={{ marginTop: 22 }}>
            <h3>{t('ac_impl_h3')}</h3>
            <p>{t('ac_impl_p')}</p>
          </div>
        </div>
      </section>

      {/* ---------------- BIO ---------------- */}
      <section className="of_section">
        <div className="of_wrap">
          <div className="of_head"><span className="of_eyebrow">{t('ac_bio_eyebrow')}</span></div>
          <div className="of_bio">
            <img className="of_bio_ph" src={process.env.PUBLIC_URL + '/images/younes-pro.jpg'} alt={t('ac_bio_name')} width="92" height="92" loading="lazy" />
            <div>
              <h3>{t('ac_bio_name')}</h3>
              <div className="role">{t('ac_bio_role')}</div>
              <p>{t('ac_bio_p')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- FAQ / OBJECTIONS ---------------- */}
      <section className="of_section alt">
        <div className="of_wrap">
          <div className="of_head center"><h2 className="of_h2">{t('ac_faq_h2')}</h2></div>
          <div className="of_faq">
            {faq.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p className="ans">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA FINAL ---------------- */}
      <section className="of_final">
        <div className="of_wrap of_final_in">
          <h2>{t('ac_final_h2')}</h2>
          <p className="of_lead">{t('ac_final_p')}</p>
          <div className="of_cta_row">
            <Link className="of_btn of_btn_primary" to="/contactUs?subject=audit">
              {t('ac_final_cta')} <span aria-hidden="true">→</span>
            </Link>
            <Link className="of_btn of_btn_ghost" to="/agences">{t('nav_agencies')}</Link>
          </div>
          <p className="of_ps">{t('ac_final_ps')}</p>
        </div>
      </section>
    </div>
  );
}
